import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Droplets, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const user = await login(email, password);
      if (user.role === "admin") {
        navigate("/admin", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      // Error is handled in AuthContext
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Veuillez entrer votre adresse email");
      return;
    }
    
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setEmailSent(true);
      toast.success("Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.");
    } catch (error) {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail("");
    setEmailSent(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1581720604719-ee1b1a4e44b1?ixlib=rb-4.1.0&auto=format&fit=crop&w=1950&q=80')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid="login-page"
    >
      {/* Overlay - darker for better text readability */}
      <div className="absolute inset-0 bg-slate-900/80"></div>

      <Card className="w-full max-w-md relative z-10 shadow-2xl" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-3">
            <img 
              src="/aquareport-logo.png" 
              alt="AquaReport" 
              className="h-32 sm:h-36 w-auto object-contain"
              data-testid="login-logo"
            />
          </div>
          <CardDescription className="text-slate-500 text-sm leading-relaxed mt-1">
            Solution digitale pour les professionnels de la recherche de fuite et de l'expertise en dégâts des eaux.
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-4">
          {!showForgotPassword ? (
            // Login Form
            <>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.fr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 text-base"
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-12 text-base pr-12"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      data-testid="toggle-password"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white font-semibold text-base"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? (
                    <div className="spinner w-5 h-5 border-white border-t-transparent" />
                  ) : (
                    "Se connecter"
                  )}
                </Button>
              </form>

              <p className="text-center text-xs text-slate-400 mt-3" data-testid="secure-connection-text">
                Connexion sécurisée – données protégées
              </p>

              <div className="mt-4 pt-4 border-t border-slate-200 text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-sky-600 hover:text-sky-700 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            </>
          ) : (
            // Forgot Password Form
            <div>
              <button
                type="button"
                onClick={resetForgotPassword}
                className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4"
                data-testid="back-to-login"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Retour à la connexion
              </button>

              {!emailSent ? (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold text-slate-900">
                      Réinitialiser le mot de passe
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Entrez votre adresse email pour recevoir un lien de réinitialisation.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="forgot-email" className="text-slate-700">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="votre@email.fr"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="h-12 text-base"
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white font-semibold text-base"
                    disabled={forgotLoading}
                    data-testid="forgot-submit-btn"
                  >
                    {forgotLoading ? (
                      <div className="spinner w-5 h-5 border-white border-t-transparent" />
                    ) : (
                      "Envoyer le lien"
                    )}
                  </Button>
                </form>
              ) : (
                <div className="text-center py-6">
                  <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Email envoyé !
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Si un compte existe avec l'adresse <strong>{forgotEmail}</strong>, vous recevrez un email avec les instructions pour réinitialiser votre mot de passe.
                  </p>
                  <p className="text-xs text-slate-400">
                    N'oubliez pas de vérifier vos spams.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForgotPassword}
                    className="mt-6"
                    data-testid="back-to-login-btn"
                  >
                    Retour à la connexion
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
